/**
 * @file not-found-view.ts
 * @description Подписи и правила вкладки «Сайт → SEO → Не найдено»
 * (phase19.16, партия «SEO-техника», v0.19.65; SB148, SB55; макет
 * uiux-seo-tech.md §3.1, тексты состояний §4). Чистые функции — их проверяют
 * тесты, вкладка лишь рисует.
 *
 *  - статус строки → пилюля по реестру тонов (commora-console §8):
 *    «переадресован → …» (успех), «правило не действует» (внимание),
 *    «адрес снова открывается» и «скрыт» (неактивно), «—» без пилюли;
 *  - кнопка в строке: «Переадресовать» у новой строки, «Изменить» у
 *    недействующего правила, у остальных кнопки нет (`rowAction`);
 *  - «Последний раз»: «сегодня», «вчера» или `дд.мм` — от последнего дня
 *    периода (сегодня по поясу магазина, его даёт сервер);
 *  - порядок строк и пустые состояния.
 *
 * Статусы — реестром `NOT_FOUND_STATUS_META` (OCP): новый статус — запись.
 */

import { NOT_FOUND_ROW_STATUS_LABELS, type NotFoundJournalDto, type NotFoundJournalRowDto, type NotFoundRowStatus, type NotFoundSort } from "@chaiberi/shared";

import { pluralRu } from "../plural";
import type { StatusTone } from "../shop/status-meta";

/** Тон пилюли статуса; `null` — статус «—», пилюли нет. */
export const NOT_FOUND_STATUS_META: Readonly<Record<NotFoundRowStatus, { tone: StatusTone; label: string } | null>> = {
  open: null,
  redirected: { tone: "success", label: NOT_FOUND_ROW_STATUS_LABELS.redirected },
  rule_inactive: { tone: "attention", label: NOT_FOUND_ROW_STATUS_LABELS.rule_inactive },
  live_again: { tone: "neutral", label: NOT_FOUND_ROW_STATUS_LABELS.live_again },
};

/** Пилюля строки: скрытая — «скрыт» поверх статуса. */
export function notFoundStatusPill(row: Pick<NotFoundJournalRowDto, "status" | "hidden" | "redirect">): { tone: StatusTone; label: string } | null {
  if (row.hidden) return { tone: "neutral", label: "скрыт" };
  const meta = NOT_FOUND_STATUS_META[row.status];
  if (!meta) return null;
  if (row.status === "redirected" && row.redirect) return { ...meta, label: `${meta.label} → ${row.redirect.targetHref}` };
  return meta;
}

/** Кнопка в строке (видна, а не в «⋯»): у «—» — добавить, у недействующего правила — изменить. */
export type NotFoundRowAction = "redirect" | "edit" | null;

export function notFoundRowAction(row: Pick<NotFoundJournalRowDto, "status" | "hidden" | "redirect">): NotFoundRowAction {
  if (row.hidden) return null;
  if (row.status === "open") return "redirect";
  if (row.status === "rule_inactive" && row.redirect) return "edit";
  return null;
}

/** «сегодня», «вчера» или `дд.мм`; `today` — последний день периода (`YYYY-MM-DD`). */
export function lastDayText(day: string, today: string): string {
  if (day === today) return "сегодня";
  const t = Date.UTC(Number(today.slice(0, 4)), Number(today.slice(5, 7)) - 1, Number(today.slice(8, 10)));
  const d = Date.UTC(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1, Number(day.slice(8, 10)));
  if (t - d === 86_400_000) return "вчера";
  return `${day.slice(8, 10)}.${day.slice(5, 7)}`;
}

/** `2026-10-25` → `25.10`. */
export function shortDate(iso: string): string {
  return `${iso.slice(8, 10)}.${iso.slice(5, 7)}`;
}

export const visitorsText = (n: number): string => `${n} ${pluralRu(n, ["посетитель", "посетителя", "посетителей"])}`;
export const botsText = (n: number): string => `${n} ${pluralRu(n, ["поисковик", "поисковика", "поисковиков"])}`;

/** Варианты порядка (сервер сортирует по числу обращений, SB148). */
export const NOT_FOUND_SORT_LABELS: Readonly<Record<NotFoundSort, string>> = {
  visitors: "больше посетителей",
  bots: "больше поисковиков",
  total: "больше всего обращений",
};

/** Порядок по умолчанию (uiux §3.1). */
export const NOT_FOUND_DEFAULT_SORT: NotFoundSort = "visitors";

/** «Журнал ведётся с 25.10 — раньше данных нет»: только если журнал короче периода. */
export function sinceText(journal: Pick<NotFoundJournalDto, "since" | "period">): string | null {
  if (!journal.since || journal.since <= journal.period.from) return null;
  return `Журнал ведётся с ${shortDate(journal.since)} — раньше данных нет.`;
}

/** Пустое состояние: за период ничего, всё обработано, ничего не нашлось по поиску. */
export type NotFoundEmptyKind = "none" | "handled" | "search";

export function notFoundEmptyKind(journal: Pick<NotFoundJournalDto, "handledCount">, query: string, all: boolean): NotFoundEmptyKind {
  if (query.trim() !== "") return "search";
  return !all && journal.handledCount > 0 ? "handled" : "none";
}

export const NOT_FOUND_EMPTY_TEXT: Readonly<Record<NotFoundEmptyKind, { title: string; description?: string }>> = {
  none: { title: "За этот период ошибок 404 не было.", description: "Сюда попадают адреса вашего сайта, по которым посетители и поисковики не нашли страницу." },
  handled: { title: "Все адреса за этот период обработаны." },
  search: { title: "Ничего не нашлось — измените поиск или период." },
};

/** Пояснение к колонке «Поисковики» (SB148: класс по заголовку, его можно подделать). */
export const BOTS_HINT =
  "Поисковики — Яндекс, Google, Bing, Mail.ru. Определяем по заголовку запроса; его можно подделать, поэтому число примерное. Журнал хранит 32 дня, без IP и данных посетителя.";

/** Пояснение к строке «прочие». */
export const OTHER_HINT =
  "В день журнал записывает первые 1000 разных адресов, остальные считаются одной строкой. Если она растёт, скорее всего сайт перебирает бот — переадресации тут не нужны.";

/** Кто действует в журнале (SB55). */
export const NOT_FOUND_READONLY_TEXT = "Переадресации добавляет и скрывает строки владелец магазина или сотрудник с правом настроек магазина.";

/** Хранилище журнала недоступно (сервер ответил 503) — свой текст, не «не удалось». */
export const NOT_FOUND_UNAVAILABLE_TEXT = "Журнал временно недоступен. Сайт работает как обычно. Попробуйте позже.";
